import { Link } from "react-router";
import { buildClassCatalogPath } from "@/utils/catalogSlug";
import {
  LANDING_CATALOG_CARD_WIDTH,
  LANDING_CATALOG_CARD,
  LANDING_CATALOG_TYPOGRAPHY,
} from "../constants/landing.constants";
import { LANDING_USE_MOCK } from "../config/landing.config";
import type { LandingCourse } from "../types/landing.types";
import { courseToListSnapshot } from "../api/landingClass.mappers";
import { saveLandingListingQuery } from "../utils/landingListingQuery";
import LandingIcon from "./LandingIcon";

interface CatalogCourseCardProps {
  course: LandingCourse;
  levelId?: string;
  level1Id?: string;
  listingQuery?: string;
  listingScopeId?: string;
}

function detailHref(
  course: LandingCourse,
  levelId?: string,
  level1Id?: string
): string {
  const parentId = level1Id?.trim() ?? "";
  const withLevel1 = (path: string) => {
    if (!parentId) return path;
    const params = new URLSearchParams({ level1Id: parentId });
    return `${path}?${params.toString()}`;
  };

  if (course.categorySlug && course.slug) {
    return withLevel1(
      buildClassCatalogPath(course.categorySlug, course.slug)
    );
  }

  const level = levelId ?? course.levelId ?? "";
  return withLevel1(`/classes/level/${level}/${course.id}`);
}

export default function CatalogCourseCard({
  course,
  levelId,
  level1Id,
  listingQuery,
  listingScopeId,
}: CatalogCourseCardProps) {
  const badge = course.audienceLabel ?? course.ageLabel;

  return (
    <article
      className={`${LANDING_CATALOG_CARD_WIDTH} ${LANDING_CATALOG_CARD.shell} ${LANDING_CATALOG_CARD.radius}`}
    >
      <div className="flex w-full flex-col gap-5">
        <div className={LANDING_CATALOG_CARD.thumbnailWrap}>
          <img
            src={course.thumbnail}
            alt={course.title}
            className={LANDING_CATALOG_CARD.thumbnail}
          />
          {badge ? (
            <span className={LANDING_CATALOG_TYPOGRAPHY.overlayBadge}>{badge}</span>
          ) : null}
        </div>

        <div className={LANDING_CATALOG_CARD.body}>
          <div className={LANDING_CATALOG_CARD.titleGroup}>
            <div className={LANDING_CATALOG_CARD.titleBlock}>
              <h3 className={LANDING_CATALOG_TYPOGRAPHY.title}>{course.title}</h3>
              <span className={LANDING_CATALOG_TYPOGRAPHY.durationBadge}>
                <LandingIcon
                  name="checkCircle"
                  className={LANDING_CATALOG_TYPOGRAPHY.durationIcon}
                />
                {course.durationDays} Days
              </span>
            </div>
            <p className={LANDING_CATALOG_TYPOGRAPHY.description}>
              {course.shortDescription}
            </p>
          </div>

          <div className={LANDING_CATALOG_TYPOGRAPHY.meta}>
            <span className={LANDING_CATALOG_TYPOGRAPHY.metaItem}>
              <LandingIcon
                name="bookOpen"
                className={LANDING_CATALOG_TYPOGRAPHY.metaIcon}
              />
              <span className={LANDING_CATALOG_TYPOGRAPHY.metaText}>
                {course.units} Units
              </span>
            </span>
            <span className={LANDING_CATALOG_TYPOGRAPHY.metaItem}>
              <LandingIcon
                name="play"
                className={LANDING_CATALOG_TYPOGRAPHY.metaIcon}
              />
              <span className={LANDING_CATALOG_TYPOGRAPHY.metaText}>
                {course.videos} videos
              </span>
            </span>
            {course.minAge != null ? (
              <span className={LANDING_CATALOG_TYPOGRAPHY.metaItem}>
                <LandingIcon
                  name="user"
                  className={LANDING_CATALOG_TYPOGRAPHY.metaIcon}
                />
                <span className={LANDING_CATALOG_TYPOGRAPHY.metaText}>
                  {course.minAge} years age
                </span>
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className={LANDING_CATALOG_CARD.ctaWrap}>
        <Link
          to={detailHref(course, levelId, level1Id)}
          state={
            LANDING_USE_MOCK
              ? listingQuery
                ? { listingQuery }
                : undefined
              : {
                  snapshot: courseToListSnapshot(course),
                  listingQuery,
                }
          }
          onClick={() => {
            if (listingScopeId && listingQuery) {
              saveLandingListingQuery(listingScopeId, listingQuery);
            }
          }}
          className={LANDING_CATALOG_TYPOGRAPHY.cta}
        >
          View Details
        </Link>
      </div>
    </article>
  );
}
